import { DIALOG_TYPES, DRAWER_TYPES, FEATURE_FLAGS } from "Common/Constants";
import { useAuthorization, useNotificationSession } from "Common/Hooks";
import type { IDrawer } from "Common/Interfaces";
import { SLIDES, STARTING_TIP_IMAGE_NAME, TERMS_OF_USE_FILE_NAME } from "Containers/Home/Constants";
import { useEffect, useMemo, useState } from "react";
import { useSetIsAcceptedMutation } from "Services/Authorization";
import { useGetLandingPageResourcesQuery } from "Services/LandingPageResources";

export const useMainLayout = () => {
  const { features, user } = useAuthorization();
  const isMessageCenterEnabled = features?.includes(FEATURE_FLAGS.MESSAGE_CENTER) ?? false;

  const [featuresDrawer, setFeaturesDrawer] = useState<IDrawer>({
    open: false,
    title: "",
    type: null,
    dataRow: null,
  });

  const {
    sessionStartTime,
    unreadCount,
    pendingSignalRItems,
    setPendingSignalRItems,
    handleNotificationDismissed,
    handleNotificationMarkedUnread,
    handleAllNotificationsDismissed,
  } = useNotificationSession(isMessageCenterEnabled);

  const handleFeaturesDrawerClose = () => {
    setFeaturesDrawer((prevState) => ({
      ...prevState,
      open: false,
    }));
  };

  const handleProfileDrawerOpen = () => {
    setFeaturesDrawer({
      open: true,
      title: "Profile",
      type: DRAWER_TYPES.VIEW,
      dataRow: null,
    });
  };

  const handleReportBugDrawerOpen = () => {
    setFeaturesDrawer({
      open: true,
      title: "Report a Bug",
      type: DRAWER_TYPES.ADD,
      dataRow: null,
    });
  };

  const handleNotificationDrawerOpen = () => {
    if (!isMessageCenterEnabled) return;

    setFeaturesDrawer({
      open: true,
      title: "Notifications",
      type: DRAWER_TYPES.NOTIFICATION,
      dataRow: null,
    });
  };

  // ---- Privacy Policy / Terms of Service / Get Started Tips dialogs ----
  // Only one of these dialogs can ever be open at a time, so a single piece
  // of state tracks which dialog (if any) is currently active.
  const [activeDialog, setActiveDialog] = useState<DIALOG_TYPES | null>(null);

  const handleOpenDialog = (type: DIALOG_TYPES) => setActiveDialog(type);
  const handleCloseDialog = () => setActiveDialog(null);

  const handlePrivacyPolicyDialogOpen = () => handleOpenDialog(DIALOG_TYPES.PRIVACY_POLICY);
  const handleTermsOfServiceDialogOpen = () => handleOpenDialog(DIALOG_TYPES.TERMS_AND_CONDITIONS);
  const handleTipsDialogOpen = () => {
    setIsStartingStep(true);
    setActiveStep(0);
    handleOpenDialog(DIALOG_TYPES.GET_STARTED_TIPS);
  };

  const hasAcceptedTerms = user?.isAcceptedTerms;
  const acceptedDate = user?.termsAcceptedDate;

  const [isChecked, setIsChecked] = useState(false);
  const [setIsAccepted, { isLoading: isLoadingTerms, isSuccess: hasAcceptedTermsSuccessfully }] = useSetIsAcceptedMutation();
  const { data: resources } = useGetLandingPageResourcesQuery();

  const normalizeKey = (value: string): string => value.trim().toLowerCase();

  const resourceMap = useMemo(() => {
    const map = new Map<string, string>();

    resources?.forEach((resource) => {
      const key = normalizeKey(resource.resourceName);
      if (key && !map.has(key)) {
        map.set(key, resource.resourceUrl);
      }
    });

    return map;
  }, [resources]);

  const startingTipImageUrl = resourceMap.get(normalizeKey(STARTING_TIP_IMAGE_NAME)) ?? "";
  const termsOfUsePdfUrl = resourceMap.get(normalizeKey(TERMS_OF_USE_FILE_NAME)) ?? "";

  const slides = useMemo(
    () =>
      SLIDES.map((slide) => ({
        ...slide,
        imageUrl: resourceMap.get(normalizeKey(slide.imageName)) ?? "",
      })),
    [resourceMap],
  );

  const [isStartingStep, setIsStartingStep] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = slides.length;
  const isFinalStep = activeStep === totalSteps - 1;

  // Force the Terms of Service dialog open until the user accepts it
  useEffect(() => {
    if (!hasAcceptedTerms) {
      handleOpenDialog(DIALOG_TYPES.TERMS_AND_CONDITIONS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once terms are accepted, move straight into the Get Started Tips dialog
  useEffect(() => {
    if (!isLoadingTerms && hasAcceptedTermsSuccessfully) {
      setIsStartingStep(true);
      setActiveStep(0);
      handleOpenDialog(DIALOG_TYPES.GET_STARTED_TIPS);
    }
  }, [hasAcceptedTermsSuccessfully, isLoadingTerms]);

  const handleTermsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const handleAcceptTerms = () => {
    if (isChecked) {
      setIsAccepted(true);
    }
  };

  const handleNextStep = () => {
    if (activeStep < totalSteps - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      handleCloseDialog();
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  return {
    featuresDrawer,
    setFeaturesDrawer,

    handleFeaturesDrawerClose,
    sessionStartTime,
    isMessageCenterEnabled,
    unreadCount,
    pendingSignalRItems,
    setPendingSignalRItems,
    handleReportBugDrawerOpen,
    handleProfileDrawerOpen,
    handleNotificationDrawerOpen,
    handleNotificationDismissed,
    handleNotificationMarkedUnread,
    handleAllNotificationsDismissed,

    // Dialogs (Privacy Policy / Terms of Service / Get Started Tips)
    isPrivacyPolicyOpen: activeDialog === DIALOG_TYPES.PRIVACY_POLICY,
    openTermsAndConditions: activeDialog === DIALOG_TYPES.TERMS_AND_CONDITIONS,
    openGetStartedTips: activeDialog === DIALOG_TYPES.GET_STARTED_TIPS,
    handleCloseDialog,
    handlePrivacyPolicyDialogOpen,
    handleTermsOfServiceDialogOpen,
    handleTipsDialogOpen,
    handleTermsCheckboxChange,
    handleAcceptTerms,
    isChecked,
    isLoadingTerms,
    hasAcceptedTerms,
    acceptedDate,
    isFinalStep,
    isStartingStep,
    setIsStartingStep,
    handleNextStep,
    handlePreviousStep,
    activeStep,
    startingTipImageUrl,
    termsOfUsePdfUrl,
    slides,
  };
};
